import React, { useReducer, useEffect, useState } from "react";
import "./Feed.scss";
import FeedContext from "./FeedContext";
import feedReducer, { getInitialState } from "./feedReducer";
import usePermissions from "../../hooks/usePermissions";
import useSyncSearch from "../../hooks/useSyncSearch";
import useGlobals from "../../hooks/useGlobals";

import CardsList from "./CardsList";
import TeaserListHeader from "./TeaserListHeader";
import TeaserListControls from "./TeaserListControls";
import TeaserPageNotifications from "./TeaserPageNotifications";
import Teasers from "./Teasers/Teasers";
import MassCopy from "./MassCopy";
import ConfirmDialog from "./ConfirmDialog";
import HasNewButton from "./HasNewButton";
import useTitle from "../../hooks/useTitle";

import * as perm from "../../utils/perm";

const confirmStart = "start";
const confirmStop = "stop";
const confirmDelete = "delete";

const getConfirmTitle = type => {
  const types = {
    [confirmStart]: "Запуск",
    [confirmStop]: "Остановка",
    [confirmDelete]: "Удаление"
  };

  return types[type];
};

const getConfirmColor = type => {
  const types = {
    [confirmStart]: "green",
    [confirmStop]: "yellow",
    [confirmDelete]: "red"
  };

  return types[type];
};

export default function Feed({ history, location }) {
  const [state, dispatch] = useReducer(
    feedReducer,
    location.search,
    getInitialState
  );
  const [initDone, setInitDone] = useState(false);

  useSyncSearch(history, location, [
    [state.teasersFilters, "teasers_"],
    [state.cardsFilters, "cards_"]
  ]);

  const title = state.teasersFilters.cid
    ? `${state.teasersFilters.cid} (CNTN.)`
    : `Общая лента (CNTN.)`;
  useTitle(title, [title]);

  const permissions = usePermissions();
  const globals = useGlobals();

  const anyCampaignAccess = perm.hasAnyCampaignAccess(permissions);

  const setCardsLoading = loading => {
    dispatch({ type: "SET_CARDS_LOADING", loading });
  };

  const setCards = cards => {
    dispatch({ type: "SET_CARDS", cards });
  };

  const setCardsSearch = search => {
    dispatch({ type: "SET_CARDS_SEARCH", search });
  };

  const setTeasersSearch = search => {
    const newSearch = { ...search };

    dispatch({ type: "SET_TEASERS_SEARCH", search: newSearch });
  };

  const resetTeasersSearch = () => {
    dispatch({
      type: "SET_TEASERS_SEARCH",
      search: {
        status: "",
        category: "",
        area: "",
        device: "",
        cid: "",
        user: "",
        geo: "",
        payout_status: ""
      }
    });
  };

  const setUserCards = cards => {
    dispatch({ type: "SET_USER_CARDS", cards });
  };

  const setGlobalCheck = globalCheck => {
    dispatch({ type: "SET_GLOBAL_CHECK", globalCheck });
  };

  const onShowUrlChange = e => {
    dispatch({ type: "SET_SHOW_URL", showUrl: e.target.checked });
  };

  const onShowDisclaimerChange = e => {
    dispatch({ type: "SET_SHOW_DISCLAIMER", showDisclaimer: e.target.checked });
  };

  const setTeasersLoading = loading => {
    dispatch({ type: "SET_TEASERS_LOADING", loading });
  };

  const setTeasers = ({ teasers, total_count }) => {
    dispatch({ type: "SET_TEASERS", teasers, total_count });
  };

  const setTotalStat = ({ total_stat }) => {
    dispatch({ type: "SET_TOTAL_STAT", total_stat });
  };

  const appendTeasers = teasers => {
    dispatch({ type: "APPEND_TEASERS", teasers });
  };

  const setPage = page => {
    dispatch({ type: "SET_TEASERS_PAGE", page });
  };

  const onTeaserCheckChange = ({ tid, checked }) => {
    dispatch({ type: "SET_TEASER_CHECKED", tid, checked });
  };

  const onTeaserChange = (tid, payload) => {
    dispatch({ type: "TEASER_CHANGE", tid, payload });
  };

  const toggleCardsOpen = () => {
    dispatch({ type: "TOGGLE_CARDS_OPEN" });
  };

  const setPayoutMode = e => {
    dispatch({ type: "SET_PAYOUT_MODE", payoutMode: e.target.checked });
  };

  const setMassCopyVisible = visible => {
    dispatch({ type: "SET_MASS_COPY_VISIBLE", visible });
  };

  const massActionDone = () => {
    dispatch({ type: "MASS_ACTION_DONE" });
  };

  const setConfirmType = confirmType => {
    dispatch({ type: "SET_CONFIRM_TYPE", confirmType });
  };

  const setHasNew = hasNew => {
    dispatch({ type: "SET_HAS_NEW", hasNew });
  };

  const appendNewTeasers = teasers => {
    dispatch({ type: "APPEND_NEW_TEASERS", teasers });
  };

  const handleCampaignSelect = item => {
    if (anyCampaignAccess) {
      setTeasersSearch({ cid: item.value });
    } else {
      if (item.value) {
        setTeasersSearch({ cid: item.value, user: "" });
      } else {
        setTeasersSearch({ cid: item.value, user: globals.user.id });
      }
    }
  };

  const clearTeasers = () => {
    dispatch({ type: "CLEAR_TEASERS" });
  };

  const syncCardsCid = () => {
    let newSearch = null;

    if (state.userCards.cards.length == 0) {
      newSearch = { cid: "", user: globals.user.id };
    } else {
      let cid = state.teasersFilters.cid;

      if (state.userCards.cards.filter(card => card.id == cid).length == 0) {
        cid = state.userCards.cards[0].id;
      }

      const user = "";
      newSearch = { cid, user };
    }

    if (
      newSearch &&
      newSearch.cid != state.teasersFilters.cid &&
      newSearch.user != state.teasersFilters.user
    ) {
      setTeasersSearch(newSearch);
    }
  };

  useEffect(() => {
    if (!anyCampaignAccess) {
      syncCardsCid();
    }
    if (state.userCards.firstLoadDone) {
      setInitDone(true);
    }
  }, [state.userCards.cards]);

  return (
    <FeedContext.Provider value={[state, dispatch]}>
      <CardsList
        search={state.cardsFilters}
        cards={state.cards}
        setLoading={setCardsLoading}
        setCards={setCards}
        permissions={permissions}
        setSearch={setCardsSearch}
        opened={state.ui.cardsOpen}
      />
      <div className={!state.ui.cardsOpen ? "fixed" : ""}>
        <TeaserListHeader
          fixed={!state.ui.cardsOpen}
          opened={state.ui.cardsOpen}
          toggleOpen={toggleCardsOpen}
          payoutMode={state.teasers.payoutMode}
          setPayoutMode={setPayoutMode}
          cardsAllowed={perm.allowAnyCards(permissions)}
          cards={state.userCards.cards}
          search={state.teasersFilters}
          setSearch={setTeasersSearch}
          setCards={setUserCards}
          permissions={permissions}
        />
        <TeaserListControls
          globalCheck={state.teasers.globalCheck}
          setGa={setGlobalCheck}
          checked={state.teasers.checked}
          search={state.teasersFilters}
          setSearch={setTeasersSearch}
          showUrl={state.teasers.showUrl}
          showDisclaimer={state.teasers.showDisclaimer}
          onShowUrlChange={onShowUrlChange}
          onShowDisclaimerChange={onShowDisclaimerChange}
          onStartClick={() => setConfirmType(confirmStart)}
          onStopClick={() => setConfirmType(confirmStop)}
          onCopyClick={() => setMassCopyVisible(true)}
          onDeleteClick={() => setConfirmType(confirmDelete)}
          userCardsLength={state.userCards.cards.length}
          totalCount={state.teasers.total_count}
          totalStat={state.teasers.total_stat}
          handleCampaignSelect={handleCampaignSelect}
        />
      </div>
      <TeaserPageNotifications />
      {initDone && (
        <Teasers
          teasers={state.teasers.list}
          search={state.teasersFilters}
          page={state.teasers.page}
          loading={state.teasers.loading}
          hasMore={state.teasers.hasMore}
          payoutMode={state.teasers.payoutMode}
          checked={state.teasers.checked}
          globalCheck={state.teasers.globalCheck}
          showUrl={state.teasers.showUrl}
          showDisclaimer={state.teasers.showDisclaimer}
          setTeasersLoading={setTeasersLoading}
          setTeasers={setTeasers}
          setTotalStat={setTotalStat}
          appendTeasers={appendTeasers}
          setPage={setPage}
          onChange={onTeaserChange}
          onCheckChange={onTeaserCheckChange}
          setHasNew={setHasNew}
          setSearch={setTeasersSearch}
          resetTeasersSearch={resetTeasersSearch}
          reloadCnt={state.teasers.reloadCnt}
        />
      )}

      <HasNewButton
        search={state.teasersFilters}
        appendNewTeasers={appendNewTeasers}
      />

      {state.ui.massCopy && (
        <MassCopy
          globalCheck={state.teasers.globalCheck}
          ids={state.teasers.checked}
          search={state.teasersFilters}
          onConfirm={options => {
            if (!options.continue) {
              massActionDone();
            }
            setMassCopyVisible(false);
          }}
          onCancel={() => {
            setMassCopyVisible(false);
          }}
        />
      )}

      {state.ui.confirmType && (
        <ConfirmDialog
          title={getConfirmTitle(state.ui.confirmType)}
          confirmColor={getConfirmColor(state.ui.confirmType)}
          confirmAction={state.ui.confirmType}
          globalCheck={state.teasers.globalCheck}
          ids={state.teasers.checked}
          search={state.teasersFilters}
          onCancel={() => {
            setConfirmType(null);
          }}
          onConfirm={() => {
            massActionDone();
            setConfirmType(null);
          }}
        />
      )}
    </FeedContext.Provider>
  );
}
